import axios from 'axios';

// API Endpoints
const GEO_LOCATION_API = '/GeoLocation/GetGeoLocation';
const LEAD_API = '/api/Lead/AddLead';

/**
 * Get user's geolocation information (country and IP)
 */
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(GEO_LOCATION_API, {
      headers: {
        'accept': 'text/plain'
      }
    });

    // Check if API response is successful
    if (response.data.isSuccess && response.data.statusCode === 200) {
      return {
        success: true,
        data: {
          ipAddress: response.data.data.ipAddress,
          country: response.data.data.countryCode,
          countryName: response.data.data.countryName,
          countryCode: response.data.data.countryCode,
          regionName: response.data.data.regionName,
          cityName: response.data.data.cityName,
          timeZone: response.data.data.timeZone
        }
      };
    } else {
      console.error('Geolocation API error:', response.data.errorMessage);
      return {
        success: false,
        data: {
          ipAddress: '0.0.0.0',
          country: 'US',
          countryName: 'United States',
          countryCode: 'US'
        }
      };
    }
  } catch (error) {
    console.error('Error fetching geo location:', error);
    return {
      success: false,
      data: {
        ipAddress: '0.0.0.0',
        country: 'US',
        countryName: 'United States',
        countryCode: 'US'
      }
    };
  }
};

/**
 * Submit lead to CRM
 * @param {Object} leadData - Lead information
 * @param {string} leadData.name - Full name
 * @param {string} leadData.email - Email address
 * @param {number} leadData.countryDialCode - Country dial code (numeric)
 * @param {string} leadData.country - Country code (e.g., 'US', 'NL')
 * @param {string} leadData.phoneNumber - Phone number (only digits, no prefix)
 * @param {string} leadData.ipAddress - IP address from geo location
 * @param {string} leadData.description - Message/description
 * @param {string} leadData.pageUrl - Current page URL
 */
export const submitLead = async (leadData) => {
  try {
    // Ensure phone number is only digits (remove any non-numeric characters)
    const cleanPhoneNumber = leadData.phoneNumber.replace(/\D/g, '');

    const payload = {
      name: leadData.name,
      email: leadData.email,
      countryDialCode: parseInt(leadData.countryDialCode),
      country: leadData.country,
      phoneNumber: cleanPhoneNumber,
      ipAddress: leadData.ipAddress,
      description: leadData.description || '',
      pageUrl: leadData.pageUrl || window.location.href
    };

    console.log('Submitting lead with payload:', payload);

    const response = await axios.post(LEAD_API, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Check if response is successful
    if (response.data.statusCode === 200 && response.data.isSuccess) {
      return {
        success: true,
        customerId: response.data.data.customerId,
        message: response.data.data.message
      };
    } else {
      return {
        success: false,
        message: response.data.errorMessage || response.data.data.message || 'Failed to submit lead'
      };
    }

  } catch (error) {
    console.error('Error submitting lead:', error);

    // Handle API error responses
    if (error.response) {
      const errorData = error.response.data;
      
      // Check for 400 or 401 errors
      if (errorData.statusCode === 400 || errorData.statusCode === 401) {
        return {
          success: false,
          message: errorData.errorMessage || errorData.data?.message || 'Validation error'
        };
      }

      return {
        success: false,
        message: errorData.errorMessage || 'Failed to submit lead. Please try again.'
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Helper function to get country dial code from country code
 */
export const getDialCodeFromCountry = (countryCode) => {
  const countryDialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'AU': 61, 'NZ': 64, 'IN': 91,
    'PK': 92, 'BD': 880, 'NL': 31, 'DE': 49, 'FR': 33, 'ES': 34,
    'IT': 39, 'PT': 351, 'BE': 32, 'CH': 41, 'AT': 43, 'SE': 46,
    'NO': 47, 'DK': 45, 'FI': 358, 'PL': 48, 'CZ': 420, 'HU': 36,
    'RO': 40, 'BG': 359, 'GR': 30, 'TR': 90, 'RU': 7, 'UA': 380,
    'IL': 972, 'SA': 966, 'AE': 971, 'QA': 974, 'KW': 965, 'OM': 968,
    'EG': 20, 'ZA': 27, 'KE': 254, 'NG': 234, 'GH': 233, 'CN': 86,
    'JP': 81, 'KR': 82, 'TH': 66, 'VN': 84, 'MY': 60, 'SG': 65,
    'PH': 63, 'ID': 62, 'BR': 55, 'MX': 52, 'AR': 54, 'CL': 56,
    'CO': 57, 'PE': 51, 'VE': 58
  };
  
  return countryDialCodes[countryCode] || 1;
};

/**
 * Navigate to application page with customer ID
 */
export const navigateToApplication = (customerId) => {
  window.location.href = `/application/main?customerid=${customerId}`;
};
